import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import {
  incomeExpenseChart,
  expenseCategoryChart,
  profitLossChart,
  creditStatusChart,
} from '@/mocks/tr/dashboard';
import { mockSales } from '@/mocks/tr/sales';
import { mockCustomers } from '@/mocks/tr/customers';
import { mockSupplierPurchases } from '@/mocks/tr/suppliers';
import { mockExpenses } from '@/mocks/tr/expenses';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
}

export default function Reports() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('sales');

  const tabs = [
    { key: 'sales', label: t('reports.dailySales'), icon: 'ri-shopping-bag-line' },
    { key: 'income', label: t('reports.monthlyIncomeExpense'), icon: 'ri-bar-chart-line' },
    { key: 'credit', label: t('reports.creditReport'), icon: 'ri-time-line' },
    { key: 'supplier', label: t('reports.supplierReport'), icon: 'ri-truck-line' },
    { key: 'expense', label: t('reports.expenseCategory'), icon: 'ri-bill-line' },
    { key: 'profit', label: t('reports.profitLoss'), icon: 'ri-line-chart-line' },
  ];

  const overdueCollections = mockCustomers.filter((c) => c.status === 'Gecikti');
  const upcomingPayments = mockSupplierPurchases.filter((p) => p.status === 'Bekliyor');

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-boutique-black">{t('reports.title')}</h1>
          <p className="text-sm text-boutique-warm mt-1">Detaylı raporları ve grafikleri görüntüleyin</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 bg-white p-2 rounded-xl border border-cream-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-boutique-black text-white'
                : 'text-boutique-warm hover:text-boutique-black hover:bg-cream-100'
            }`}
          >
            <i className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-cream-200 p-6">
        {activeTab === 'sales' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-boutique-black text-sm">{t('reports.dailySales')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-cream-50 border-b border-cream-200">
                    <th className="text-left px-4 py-3 font-semibold text-boutique-black">Tarih</th>
                    <th className="text-left px-4 py-3 font-semibold text-boutique-black">Müşteri</th>
                    <th className="text-right px-4 py-3 font-semibold text-boutique-black">Tutar</th>
                    <th className="text-left px-4 py-3 font-semibold text-boutique-black">Ödeme</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-100">
                  {mockSales.slice(0, 7).map((s) => (
                    <tr key={s.id} className="hover:bg-cream-50 transition-colors">
                      <td className="px-4 py-3 text-boutique-warm">{s.sale_date}</td>
                      <td className="px-4 py-3 font-medium text-boutique-black">{s.customer_name}</td>
                      <td className="px-4 py-3 text-right font-semibold text-boutique-black">{formatCurrency(s.total_amount)}</td>
                      <td className="px-4 py-3 text-boutique-warm">{s.payment_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'income' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-boutique-black text-sm">{t('reports.monthlyIncomeExpense')}</h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={incomeExpenseChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F5E3C5" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#B8C4B8" />
                <YAxis tick={{ fontSize: 12 }} stroke="#B8C4B8" tickFormatter={(v) => `${v / 1000}K`} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ borderRadius: '8px', border: '1px solid #F5E3C5', fontSize: '12px' }} />
                <Line type="monotone" dataKey="gelir" stroke="#D4A843" strokeWidth={2} dot={{ fill: '#D4A843', r: 3 }} />
                <Line type="monotone" dataKey="gider" stroke="#C9A8A0" strokeWidth={2} dot={{ fill: '#C9A8A0', r: 3 }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {activeTab === 'credit' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-boutique-black text-sm">{t('reports.creditReport')}</h3>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={creditStatusChart}
                  cx="50%"
                  cy="50%"
                  outerRadius={130}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {creditStatusChart.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ borderRadius: '8px', border: '1px solid #F5E3C5', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {activeTab === 'supplier' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-boutique-black text-sm">{t('reports.supplierReport')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-cream-50 border-b border-cream-200">
                    <th className="text-left px-4 py-3 font-semibold text-boutique-black">Tedarikçi</th>
                    <th className="text-right px-4 py-3 font-semibold text-boutique-black">Toplam</th>
                    <th className="text-right px-4 py-3 font-semibold text-boutique-black">Ödenen</th>
                    <th className="text-right px-4 py-3 font-semibold text-boutique-black">Kalan</th>
                    <th className="text-left px-4 py-3 font-semibold text-boutique-black">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-100">
                  {upcomingPayments.map((p) => (
                    <tr key={p.id} className="hover:bg-cream-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-boutique-black">{p.product_description}</td>
                      <td className="px-4 py-3 text-right font-semibold text-boutique-black">{formatCurrency(p.total_amount)}</td>
                      <td className="px-4 py-3 text-right text-green-600">{formatCurrency(p.paid_amount)}</td>
                      <td className="px-4 py-3 text-right text-red-600">{formatCurrency(p.remaining_amount)}</td>
                      <td className="px-4 py-3 text-boutique-warm">{p.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'expense' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-boutique-black text-sm">{t('reports.expenseCategory')}</h3>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={expenseCategoryChart}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={130}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {expenseCategoryChart.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ borderRadius: '8px', border: '1px solid #F5E3C5', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {activeTab === 'profit' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-boutique-black text-sm">{t('reports.profitLoss')}</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={profitLossChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F5E3C5" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#B8C4B8" />
                <YAxis tick={{ fontSize: 12 }} stroke="#B8C4B8" tickFormatter={(v) => `${v / 1000}K`} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ borderRadius: '8px', border: '1px solid #F5E3C5', fontSize: '12px' }} />
                <Bar dataKey="kazanc" fill="#D4A843" radius={[4, 4, 0, 0]} />
                <Bar dataKey="zarar" fill="#C9A8A0" radius={[4, 4, 0, 0]} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Bottom Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <h3 className="font-semibold text-boutique-black text-sm mb-4">{t('reports.overdueCollections')}</h3>
          <div className="space-y-3">
            {overdueCollections.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-500 font-semibold text-xs">
                    {c.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-boutique-black">{c.name}</p>
                    <p className="text-xs text-red-500">{c.phone}</p>
                  </div>
                </div>
                <p className="text-sm font-semibold text-red-600">{formatCurrency(c.remaining_debt)}</p>
              </div>
            ))}
            {overdueCollections.length === 0 && (
              <p className="text-sm text-boutique-warm text-center py-4">Gecikmiş tahsilat bulunmuyor</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <h3 className="font-semibold text-boutique-black text-sm mb-4">{t('reports.upcomingPayments')}</h3>
          <div className="space-y-3">
            {upcomingPayments.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-gold-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gold-100 flex items-center justify-center text-gold-500">
                    <i className="ri-truck-line text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-boutique-black">{p.product_description}</p>
                    <p className="text-xs text-gold-600">Vade: {p.due_date}</p>
                  </div>
                </div>
                <p className="text-sm font-semibold text-boutique-black">{formatCurrency(p.remaining_amount)}</p>
              </div>
            ))}
            {upcomingPayments.length === 0 && (
              <p className="text-sm text-boutique-warm text-center py-4">Yaklaşan ödeme bulunmuyor</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}